import 'leaflet/dist/leaflet.css';

import L from 'leaflet';
import React, { useEffect, useMemo, useRef } from 'react';

import { lambert93ToWGS84 } from '../../../utils/mapUtils';

interface ParcelleSVGProps {
  geometry: {
    type: string;
    coordinates: number[][][][];
  };
  maxHeight?: number; // New prop for maximum height
  showSatellite?: boolean; // New prop for satellite view
}

const ParcelleSVG: React.FC<ParcelleSVGProps> = ({ geometry, maxHeight = 200, showSatellite = false }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<L.Map | null>(null);

  const svgContent = useMemo(() => {
    if (!geometry || !geometry.coordinates || geometry.coordinates.length === 0 || geometry.coordinates[0].length === 0) {
      return null;
    }

    const coordinates = geometry.coordinates[0][0];
    if (coordinates.length < 3) {
      return null; // Need at least 3 points to form a polygon
    }

    // Calculate bounding box
    const [minX, minY, maxX, maxY] = coordinates.reduce(
      ([minXReduced, minYReduced, maxXReduced, maxYReduced], [x, y]) => [
        Math.min(minXReduced, x),
        Math.min(minYReduced, y),
        Math.max(maxXReduced, x),
        Math.max(maxYReduced, y),
      ],
      [Infinity, Infinity, -Infinity, -Infinity],
    );

    const width = maxX - minX;
    const height = maxY - minY;

    // Calculate scaling factor to normalize the size
    const desiredSize = 1000; // Desired size of the larger dimension
    const scaleFactor = desiredSize / Math.max(width, height);

    // Apply scaling and add padding
    const padding = desiredSize * 0.1; // 10% padding
    const scaledWidth = width * scaleFactor;
    const scaledHeight = height * scaleFactor;
    const viewBox = `0 0 ${scaledWidth + (padding * 2)} ${scaledHeight + (padding * 2)}`;

    // Create path with scaled, translated, and Y-flipped coordinates
    const path = `${coordinates.map((coord, index) => {
      const x = ((coord[0] - minX) * scaleFactor) + padding;
      const y = scaledHeight - ((coord[1] - minY) * scaleFactor) + padding; // Flip Y-axis
      return `${index === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ')}Z`;

    // Determine fill color based on geometry type
    // everything is set to blue for now but you can change the colors as needed
    const fillColor = {
      polygon: '#8da0cb',
      multipolygon: '#8da0cb',
    }[geometry.type.toLowerCase()] || '#8da0cb'; // Blue for other types

    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} className="h-auto w-full" style={{ maxHeight: `${maxHeight}px` }}>
        <path d={path} fill={fillColor} stroke="#000000" strokeWidth={desiredSize / 500} />
      </svg>
    );
  }, [geometry, maxHeight]);

  useEffect(() => {
    if (showSatellite && mapRef.current && geometry && geometry.coordinates && geometry.coordinates.length > 0) {
      if (!leafletMap.current) {
        leafletMap.current = L.map(mapRef.current, {
          zoomControl: false, // Hide zoom controls
          attributionControl: false, // Disable attribution
        }).setView([0, 0], 13);

        L.tileLayer('https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
          maxZoom: 21,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
          attribution: '', // Empty string to remove attribution text
        }).addTo(leafletMap.current);
      }

      const coordinates = geometry.coordinates[0][0].map((coord) => lambert93ToWGS84(coord[0], coord[1]));
      const polygon = L.polygon(coordinates, { color: 'blue' }).addTo(leafletMap.current);
      leafletMap.current.fitBounds(polygon.getBounds(), { maxZoom: 18 });
    }

    return () => {
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
      }
    };
  }, [geometry, showSatellite]);

  if (showSatellite) {
    return <div ref={mapRef} style={{ height: `${maxHeight}px`, width: '100%', zIndex: 10 }} />;
  }

  if (!svgContent) {
    return <div className="my-8 text-sm">Impossible de générer l'aperçu de la parcelle.</div>;
  }

  return (
    <div className="parcelle-svg" style={{ maxHeight: `${maxHeight}px`, overflow: 'hidden' }}>
      {svgContent}
    </div>
  );
};

export default React.memo(ParcelleSVG);
